import { useEffect, useState, useMemo } from 'react';
import { getPricing } from '../api/resourceApi';
import { getAfaSettings } from '../api/generalSettingsApi';

// Default battery options (used as fallback if DB has no battery pricing)
const DEFAULT_BATTERY_OPTIONS = [
    { type: '14.3', label: '14.3kWh', kwhEach: 14.3, cashEach: 7400, ccEach: 8000 },
    { type: '16', label: '16kWh', kwhEach: 16, cashEach: 8200, ccEach: 8900 },
    { type: '5', label: '5kWh', kwhEach: 5, cashEach: 3000, ccEach: 3280 },
];

const PANEL_WATT = 650;
const YIELD_PER_KWP = 3.5; // kWh/kWp/day
const BATT_RTE = 0.9; // round-trip efficiency
const BATT_DOD = 0.9; // depth of discharge
const DAY_PCT = 0.30;

// Safety ratio kWp/kW (inverter): no-battery installs are capped tighter
// because excess generation can't be stored, only clipped or exported.
// With a battery the headroom can absorb the over-array, so the cap loosens.
const RATIO_CAP_NO_BATT = 1.45;
// Raised 1.85 → 1.95 and made INCLUSIVE: "1.95 and below still can" (user 2026-08-27).
// RSC only — CSC keeps its own ratio rules and never imports these.
const RATIO_CAP_WITH_BATT = 1.95;

// The with-battery cap is an INCLUSIVE maximum, compared at 2 dp — the same precision
// the ratio label shows. Both matter:
//   • 15.6 kWp on 8 kW is EXACTLY 1.95, so the old `ratio < cap` test rejected the very
//     config the cap is meant to permit. Raising the number alone would not have fixed it.
//   • a ratio that reads 1.95 on screen must behave as 1.95 even when the float lands a
//     hair above, so what the agent sees is what gets enforced.
// The no-battery cap deliberately keeps its original exclusive behaviour (must be UNDER
// 1.45) — nothing was asked to change there, and loosening it would re-price live quotes.
const capOk = (ratio, cap, hasBattery) => hasBattery
    ? Math.round(ratio * 100) / 100 <= cap
    : ratio < cap;
// Above this the array is so far over the inverter that the battery on the table
// can no longer soak up the midday peak — the inverter clips it and that
// generation is simply LOST. Still under the 1.95 hard cap, so it isn't an
// "unsafe" config; it's a "size the battery up" advisory. (user 2026-08-12)
const RATIO_MORE_BATT = 1.75;
// How far the AUTOMATIC upgrade may go past SEDA. One step: 3P 15 -> 20 kW, the
// RM500 "Inverter Upgrade 3p15 to 3p20" add-on. Above 15 kW the job needs CCC and a
// chargeman, which the user has accepted for this band — "if 20kw then need ccc follow
// same rules. Just apply to these few pcs" (2026-10-10). The cap is what keeps "these
// few" honest: without it a large array would chain itself 15 -> 20 -> 25 -> 30 on the
// add-ons that exist, and auto-propose something nobody intended to sell.
// Anything beyond 20 kW stays MANUAL, behind the pill that already warns about CCC.
const AUTO_UPGRADE_MAX_3P = 20;

// SEDA hard caps on inverter size — anything above needs CCC / customer accepts risk.
// Auto-proposals stay within these so we never recommend a config the customer can't
// register through SEDA without a chargeman.
const SEDA_CAP_1P = 5;
const SEDA_CAP_3P = 15;

// 3-phase inverter upgrade chain costs (RM) — sourced from the resourceaddon table
// ("Inverter Upgrade 3pX to 3pY"). Used by the Bill-tab auto-proposal to fold an
// inverter-bump cost into Opt A's Price + ROI when the right-sized panel count's
// natural inverter would breach the no-battery ratio cap (1.45). Stays inside the
// 3P SEDA ceiling of 15 kW; anything above needs CCC and is left to manual edit.
const INV_UPGRADE_3P = {
    5:  { 8: 300, 10: 600, 12: 1200, 15: 2000 },   // 5→8→10→12→15 chained
    8:  { 10: 300, 12: 900, 15: 1700 },
    10: { 12: 600, 15: 1400 },
    12: { 15: 800 },
};
const SEDA_LADDER_3P = [5, 8, 10, 12, 15];
// Given a current (natural) 3P inverter kW and a desired ratio cap, return
// { kw, cost } for the smallest inverter inside SEDA (≤15kW) that satisfies it.
// Returns null if no SEDA-bounded inverter can clear the cap.
function pickUpgrade3P(kwp, naturalKw, ratioCap) {
    if (!naturalKw || naturalKw <= 0) return null;
    if (kwp / naturalKw < ratioCap) return { kw: naturalKw, cost: 0 }; // no upgrade needed
    for (const kw of SEDA_LADDER_3P) {
        if (kw <= naturalKw) continue;
        if (kw > SEDA_CAP_3P) break;
        if (kwp / kw >= ratioCap) continue;
        const cost = INV_UPGRADE_3P[naturalKw]?.[kw];
        if (cost == null) continue;
        return { kw, cost };
    }
    return null;
}
const parseInverterKw = (str) => {
    if (!str) return 0;
    const m = /(\d+(?:\.\d+)?)\s*k/i.exec(String(str));
    return m ? parseFloat(m[1]) : 0;
};

// Safety ratio (panel kWp / inverter kW) display contract shared by the Bill,
// System Size, and Proposal tabs. No battery must stay < 1.45; a battery buffers
// the over-array so the cap loosens to <= 1.95. Returns null when the inverter is
// unknown (caller renders nothing). `batteryResolves` = the battery is what keeps
// this size legal (no-batt would breach 1.45 but it's within the battery cap).
function computeSafetyRatio(kwp, inverterKw, hasBattery) {
    const kw = Number(inverterKw) || 0, p = Number(kwp) || 0;
    if (kw <= 0 || p <= 0) return null;
    const ratio = p / kw;
    const cap = hasBattery ? RATIO_CAP_WITH_BATT : RATIO_CAP_NO_BATT;
    const ok = capOk(ratio, cap, hasBattery);
    return {
        ratio, ok, cap, hasBattery,
        label: `kWp/kW ${ratio.toFixed(2)}`,
        color: ok ? '#16A34A' : '#F59E0B',
        maxKwpNoBatt: Math.floor(RATIO_CAP_NO_BATT * kw * 10) / 10,
        maxKwpWithBatt: Math.floor(RATIO_CAP_WITH_BATT * kw * 10) / 10,
        batteryResolves: ratio >= RATIO_CAP_NO_BATT && capOk(ratio, RATIO_CAP_WITH_BATT, true),
        // Over-array past 1.75: the battery must be BIGGER or the excess is clipped.
        // ONLY flagged once the config also breaches its safety cap. Anything still
        // INSIDE the cap (e.g. 1P 9.1kWp on 5kW + 16kWh = 1.82) is a legitimate
        // sizing we sell every day — nagging "需加大电池" on a safe config just scared
        // sales off their own quote. Within-cap now falls through to the milder
        // "battery required for safety ratio" chip instead. (user 2026-08-17)
        needsMoreBattery: !ok && ratio > RATIO_MORE_BATT,
    };
}

// TNB tariff: bill -> kWh
function billToKwh(bill) {
    if (bill <= 0) return 0;
    // Exact inverse of calcC2Bill (import-only) via binary search, so the two
    // directions tally: kWh → RM (calcC2Bill) and RM → kWh (here) round-trip to
    // the same C2 tariff. (Old code used the legacy tiered domestic tariff, which
    // did NOT match the C2 bill shown on the System Size tab.)
    let lo = 0, hi = 2000;
    while (calcC2Bill(hi, 0) < bill && hi < 100000) hi *= 2;
    for (let i = 0; i < 40; i++) {
        const mid = (lo + hi) / 2;
        if (calcC2Bill(mid, 0) < bill) lo = mid; else hi = mid;
    }
    return Math.round((lo + hi) / 2);
}

// kWh -> estimated bill. Uses the SAME C2 tariff as calcC2Bill so the Bill-tab
// option brackets, the displayed bill, and the saving %/ROI all align (previously
// this used the legacy tiered tariff, which mis-bracketed and made Option B
// over-size the battery vs the exact System Size calc).
function kwhToBill(kwh) {
    if (kwh <= 0) return 0;
    return Math.round(calcC2Bill(kwh, 0) * 100) / 100;
}

// C2 Tariff bill calculation — EXACT same as ATAP tab's calcAtapBill
const ATAP_E_LOW = 27.03, ATAP_E_HIGH = 37.03, ATAP_CAP = 4.55, ATAP_NET = 12.85;
// AFA (Automatic Fuel Adjustment): per-kWh surcharge that changes MONTHLY, set from
// General Settings (Super Admin) via setAfaRate(). Module-level so every calcC2Bill
// caller picks it up with no signature change; callers that memoize bills add `afaRate`
// to their deps so they recompute when it loads. EXEMPT for usage <= AFA_EXEMPT_KWH (800) kWh/month.
// ST base INCLUDES AFA; KWTBB base EXCLUDES it (verified vs TNB 1000 & 1600 kWh bills).
let AFA_RATE = 0; // sen/kWh
// AFA applies only when monthly import is ABOVE this; once above, it is charged on ALL
// imported kWh (not just the excess). Raised 600 → 800 kWh (user 2026-09-21). Single
// source — every residential AFA calculation reads this.
export const AFA_EXEMPT_KWH = 800;
export function setAfaRate(r) { AFA_RATE = Number(r) || 0; }
export function getAfaRate() { return AFA_RATE; }
// AFA on/off toggle (RSC UI) — when OFF, AFA is excluded from the bill estimate.
// Default OFF (No AFA) per user 2026-07-02 — sales quote without the fuel surcharge.
let AFA_ON = false;
export function setAfaOn(b) { AFA_ON = b !== false; }
export function getAfaOn() { return AFA_ON; }

// Run `fn` with a specific AFA rate (sen/kWh) switched ON, then put the shared
// singletons back exactly as they were — even if fn throws. The Bill Analysis report
// computes against the AFA of ITS bill month while the RSC page keeps its own toggle;
// without the restore, using one would silently change the other's figures.
// A negative rate (AFA rebate) is honoured. Synchronous callers only. (user 2026-09-11)
export function withAfa(senRate, fn) {
    const prevRate = AFA_RATE, prevOn = AFA_ON;
    AFA_RATE = Number(senRate) || 0;
    AFA_ON = AFA_RATE !== 0;
    try { return fn(); } finally { AFA_RATE = prevRate; AFA_ON = prevOn; }
}

// The stored AFA rate (sen/kWh) for EXACTLY this month key ("2026-08"), or null when
// none is on file. Deliberately no fallback to a nearby, earlier or current month: a
// month with no rate means no AFA, and the Bill Analysis counts it as 0. The month key
// comes from Month + Year pickers, so no free-text parsing is needed. (user 2026-09-11)
export function afaRateForMonth(rates, key) {
    if (!key) return null;
    const hit = (rates || []).find(r => r && r.month === key);
    return hit ? (Number(hit.rate) || 0) : null;
}
// EE Incentive tiers (effective 1 July 2025): flat rate per kWh based on total consumption band
const EE_TIERS = [[200,-25],[250,-24.5],[300,-22.5],[350,-21],[400,-17],[450,-14.5],[500,-12],[550,-10.5],[600,-9],[650,-7.5],[700,-5.5],[750,-4.5],[800,-4],[850,-2.5],[900,-1],[1000,-0.5]];
function getEeRate(kwh) { if (kwh <= 0 || kwh > 1000) return 0; for (const [cap, rate] of EE_TIERS) { if (kwh <= cap) return rate; } return 0; }
// Full component breakdown (energy/capacity/network/retail/AFA/EE/SST/KWTBB/export
// credit) for a domestic C2/ATAP bill. calcC2Bill returns just the grand total; this
// exposes the pieces for the Bill-Analysis "what's in your bill" explainer — SAME math,
// single source. (user 2026-08-10)
export function calcC2BillDetail(importKwh, exportKwh) {
    const zero = { energy: 0, cap: 0, net: 0, retail: 0, afa: 0, eeIncentive: 0, st: 0, kwtbb: 0, expCr: 0, eeSolar: 0, grand: 0 };
    if (importKwh <= 0 && exportKwh <= 0) return zero;
    // C2 energy is STEPPED — once total kWh > 1500, the ENTIRE usage is billed at
    // the high rate (not just the portion above 1500). Confirmed by user 2026-05-29.
    // Side effect: bills in the RM 720–880 band have no clean kWh inverse; billToKwh
    // returns the lower edge (1500). That's accepted — sales can override either field.
    const e = importKwh > 1500 ? importKwh * ATAP_E_HIGH / 100 : importKwh * ATAP_E_LOW / 100;
    const cap = importKwh * ATAP_CAP / 100, net = importKwh * ATAP_NET / 100;
    const retail = importKwh > 600 ? 10 : 0;
    // AFA: per-kWh surcharge, exempt <= AFA_EXEMPT_KWH. Flows into the base + ST; NOT KWTBB.
    const afa = (AFA_ON && importKwh > AFA_EXEMPT_KWH) ? importKwh * AFA_RATE / 100 : 0;
    const eeRate = getEeRate(importKwh);
    const eeIncentive = importKwh * eeRate / 100;
    const eeSolar = (eeIncentive < 0 && exportKwh > 0) ? (exportKwh > importKwh ? -eeIncentive : -exportKwh * (eeIncentive / importKwh)) : 0;
    const total = e + cap + net + retail + afa + eeIncentive;
    const stFrac = importKwh > 0 ? Math.max(0, importKwh - 600) / importKwh : 0;
    const st = (stFrac * (total - retail) + retail) * 0.08;
    const kwtbb = importKwh <= 300 ? 0 : ((total - afa) - retail) * 0.016;
    const expCr = exportKwh > 0 ? (importKwh < 1501 ? -exportKwh * ATAP_E_LOW / 100 : -exportKwh * ATAP_E_HIGH / 100) : 0;
    return { energy: e, cap, net, retail, afa, eeIncentive, st, kwtbb, expCr, eeSolar, grand: total + st + kwtbb + expCr + eeSolar };
}
function calcC2Bill(importKwh, exportKwh) {
    return calcC2BillDetail(importKwh, exportKwh).grand;
}

// NEM 1:1 net metering (Domestic, non-ToU). DIFFERS from ATAP/calcC2Bill in one way: export is
// credited at the FULL rate = energy + capacity + network (not energy only). Everything on the
// import side — energy/cap/net/retail/AFA/EE, Service Tax and KWTBB — is on the GROSS import,
// same as ATAP. NEM credit that exceeds the month's charge banks (Baki NEM) and carries forward
// to 31 Dec.
//
// SERVICE TAX is on the GROSS import above 600 kWh, not on net consumption. Verified against an
// Aug 2026 NEM bill (import 956, export 715, AFA RM0.038 → Caj Semasa RM173.33, model 173.31 —
// TNB rounds per line): it charges SST RM14.39 on the 356 kWh above 600 although net usage was
// only 241 kWh. This REPLACES the 2026-07-11 "ST on NET" rule, which reproduced a May 2026 bill
// (import 1112, export 647 → RM239.85) exactly — TNB's treatment evidently changed between those
// two bills, and the current bill wins. (user 2026-09-11)
export function calcNemBill(importKwh, exportKwh) {
    if (importKwh <= 0 && exportKwh <= 0) return 0;
    const eRate = importKwh > 1500 ? ATAP_E_HIGH : ATAP_E_LOW;
    const e = importKwh * eRate / 100;
    const cap = importKwh * ATAP_CAP / 100, net = importKwh * ATAP_NET / 100;
    const retail = importKwh > 600 ? 10 : 0;
    const afa = (AFA_ON && importKwh > AFA_EXEMPT_KWH) ? importKwh * AFA_RATE / 100 : 0;
    const eeRate = getEeRate(importKwh);
    const eeIncentive = importKwh * eeRate / 100;
    const total = e + cap + net + retail + afa + eeIncentive;
    const expCr = exportKwh > 0 ? -exportKwh * (eRate + ATAP_CAP + ATAP_NET) / 100 : 0;   // full-rate credit
    const stFrac = importKwh > 0 ? Math.max(0, importKwh - 600) / importKwh : 0;
    const st = (stFrac * (total - retail) + retail) * 0.08;   // ST on GROSS import above 600 (see above)
    const kwtbb = importKwh <= 300 ? 0 : ((total - afa) - retail) * 0.016;
    const eeSolar = (eeIncentive < 0 && exportKwh > 0) ? (exportKwh > importKwh ? -eeIncentive : -exportKwh * (eeIncentive / importKwh)) : 0;
    return total + st + kwtbb + expCr + eeSolar;
}

// Battery sizing caps for the Consolidated/manual UI. Mirrors the limits the
// candidate generator applies internally (line ~207) so the per-row dropdown
// can show ALL battery sizes but flag oversized picks. daily caps are kWh/day:
//   - dailyNight = (usage/30) * (1-dayPct)  — battery only serves night use
//   - dailyExport = max(0, kwp*YIELD - dayUsage/day)  — only what solar overflows
// maxKwh = min(dailyNight, dailyExport) * 1.1, matching generateCandidates'
// slack. Returns null if usageKwh or kwp aren't usable yet.
function computeBatteryCaps(usageKwh, dayPct, kwp) {
    const u = Number(usageKwh) || 0, dp = Number(dayPct) || 0, p = Number(kwp) || 0;
    if (u <= 0 || p <= 0) return null;
    const dailyTotal = u / 30;
    const dailyNight = dailyTotal * (1 - dp);
    const dailyGen = p * YIELD_PER_KWP;
    const dailyExport = Math.max(0, dailyGen - dailyTotal * dp);
    const maxKwh = Math.min(dailyNight, dailyExport) * 1.1;
    return { dailyNight, dailyExport, maxKwh };
}

// Calculate monthly saving — EXACT same flow as ATAP tab
// Uses same rounding steps: Math.round on import/export before tariff calc
// bill parameter: the customer's actual TNB bill (used as cap)
// dayPct: daytime usage fraction (0..1). Defaults to the module DAY_PCT (0.30)
// so callers that don't care about customer-specific patterns keep the
// existing behaviour. Pass a custom dayPct (e.g. from a UI input) to
// recompute savings for a different consumption profile.
function calcSaving(kwp, usageKwh, battKwh, bill, dayPct = DAY_PCT) {
    const monthlyGen = kwp * YIELD_PER_KWP * 30;
    const dayUsage = usageKwh * dayPct;
    const nightUsage = usageKwh - dayUsage;
    const storage = battKwh * BATT_DOD * 30;
    const solarAfter = monthlyGen - dayUsage;
    const battIn = solarAfter > 0 ? Math.min(storage, solarAfter) : 0;
    const battOut = battIn * BATT_RTE;
    const exportKwh = Math.max(0, solarAfter - battIn);
    const newImportRaw = solarAfter < 0 ? Math.max(0, nightUsage - battOut) - solarAfter : Math.max(0, nightUsage - battOut);
    const newImport = Math.round(newImportRaw);
    const exportRnd = Math.min(Math.round(exportKwh), newImport);
    const origBill = calcC2Bill(usageKwh, 0);
    const newBill = calcC2Bill(newImport, exportRnd);
    const saving = Math.round((origBill - newBill) * 100) / 100;
    return bill ? Math.min(saving, bill) : saving;
}

// Generate all possible system + battery combos for a given bill
// phaseMode: 'single' or '3phase'
// dayPct: daytime usage fraction (0..1). Defaults to module DAY_PCT (0.30).
// Pass a custom dayPct to regenerate candidates for a customer-specific pattern.
function generateCandidates(pricingRows, usageKwh, bill, battOption, battQty, phaseMode, dayPct = DAY_PCT) {
    const { type, kwhEach, cashEach, ccEach } = battOption;
    const battKwhTotal = Math.round(kwhEach * battQty * 10) / 10;
    const battCashTotal = cashEach * battQty;
    const battCcTotal = ccEach * battQty;
    const is3p = phaseMode === '3phase';

    const candidates = [];
    for (const row of pricingRows) {
        const cashSingle = n(row, 'cashPrice', 'CashPrice');
        const cc36Single = n(row, 'cc36Price', 'cC36Price', 'CC36Price');
        const cash3p = n(row, 'cash3p8k', 'Cash3p8k');
        const cc36_3p = n(row, 'cc36_3p8k', 'cC36_3p8k', 'CC36_3p8k');

        // Filter by phase mode
        if (is3p && cash3p <= 0) continue;     // 3-phase: must have 3P pricing
        if (!is3p && cashSingle <= 0) continue; // single: must have 1P pricing

        // Safety ratio: kWp / inverter kW must stay within cap. The cap is
        // tighter without a battery (no buffer for excess generation).
        const inverterStr = is3p ? (row.inverterSize3p || row.InverterSize3p) : (row.inverterSize || row.InverterSize);
        const inverterKw = parseInverterKw(inverterStr);
        if (inverterKw <= 0) continue; // skip rows where inverter is missing — can't verify ratio
        // SEDA cap: auto-proposals must use an inverter the customer can register
        // without CCC. 1P ≤ 5 kW, 3P ≤ 15 kW. Above this requires a manual upgrade
        // (still available via the per-row dropdown), but we don't auto-suggest it.
        if (inverterKw > (is3p ? SEDA_CAP_3P : SEDA_CAP_1P)) continue;
        const kwp = Number(row.energy);
        const ratio = kwp / inverterKw;
        const ratioCap = battQty > 0 ? RATIO_CAP_WITH_BATT : RATIO_CAP_NO_BATT;
        if (!capOk(ratio, ratioCap, battQty > 0)) continue;

        // Battery sizing rules — avoid oversized battery suggestions:
        //   - cap by daily nighttime usage (battery only serves night)
        //   - cap by daily export (battery only charges from what solar overflows)
        // Both convert monthly totals to daily; allow a small slack (×1.1) so the
        // boundary case (battKwh ≈ daily cap) still surfaces.
        if (battQty > 0) {
            const dailyTotal = usageKwh / 30;
            const dailyNight = dailyTotal * (1 - dayPct);
            const dailyGen = kwp * YIELD_PER_KWP;
            const dailyExport = Math.max(0, dailyGen - dailyTotal * dayPct);
            if (battKwhTotal > dailyNight * 1.1) continue;   // night-use cap
            if (battKwhTotal > dailyExport * 1.1) continue;  // export cap
        }

        const basePrice = is3p ? cash3p : cashSingle;
        const baseCc = is3p ? cc36_3p : cc36Single;
        const totalPrice = basePrice + battCashTotal;
        const ccPrice = baseCc + battCcTotal;
        const savingMo = calcSaving(kwp, usageKwh, battKwhTotal, bill, dayPct);
        if (savingMo <= 0) continue;

        const roi = Math.round(totalPrice / savingMo / 12 * 100) / 100;
        const yieldRate = Math.round(savingMo * 12 / totalPrice * 10000) / 100;
        const savingPct = bill > 0 ? savingMo / bill : 0;

        candidates.push({
            kwh: usageKwh, bill,
            battType: type, battQty, battKwhEach: kwhEach, battKwhTotal,
            battUnitPrice: cashEach,
            // Panel wattage follows the ACTIVE price list per row (kWp ÷ panels) — a
            // 620W snapshot yields 620, the live 640W list yields 640 — instead of the
            // fixed PANEL_WATT constant, so the quotation label matches the kWp.
            kwp, panels: row.panelQty,
            panelWatt: (Number(row.panelQty) > 0 ? Math.round(kwp * 1000 / Number(row.panelQty)) : PANEL_WATT),
            basePrice, totalPrice, ccPrice,
            totalPrice3p: cash3p > 0 ? cash3p + battCashTotal : 0,
            ccPrice3p: cc36_3p > 0 ? cc36_3p + battCcTotal : 0,
            savingMo, roi, yieldRate, savingPct,
        });
    }
    return candidates;
}

// Generate optimized packages for all bill levels.
// savingThreshold defaults to 0.60 — used by the "Standard" (Option B) option:
// the lowest-ROI candidate that still saves >= 60% of the bill.
// "Fastest ROI" is forced to 0-battery (smallest-system payback).
// "Max Saving" picks the highest monthly saving with fastest ROI as tiebreak.
function generatePackages(pricingRows, battOption, battQties, phaseMode, savingThreshold = 0.60) {
    // Generate bill levels in 50kWh steps from 300 to 2000
    const bills = [];
    for (let kwh = 300; kwh <= 2000; kwh += 50) {
        bills.push({ kwh, bill: kwhToBill(kwh) });
    }

    const packages = [];

    for (const { kwh, bill } of bills) {
        // Collect candidates across all battery quantities (including 0)
        const allCandidates = [];

        // No battery option
        const noBatt = generateCandidates(pricingRows, kwh, bill, { type: battOption.type, kwhEach: 0, cashEach: 0, ccEach: 0 }, 0, phaseMode);
        allCandidates.push(...noBatt.map(c => ({ ...c, battType: battOption.type, battQty: 0, battKwhEach: 0, battKwhTotal: 0 })));

        // With battery options
        for (const qty of battQties) {
            const candidates = generateCandidates(pricingRows, kwh, bill, battOption, qty, phaseMode);
            allCandidates.push(...candidates);
        }

        if (allCandidates.length === 0) continue;

        // ROI ("Fastest ROI"): must be a 0-battery system. The cheapest payback
        // ignores battery cost; if no 0-batt candidate exists for this bill, skip.
        const roiPool = allCandidates.filter(c => c.battQty === 0);
        const roiSorted = roiPool.sort((a, b) => a.roi - b.roi);
        if (roiSorted.length > 0) packages.push({ ...roiSorted[0], optType: 'roi' });

        // Option B ("Standard"): >= savingThreshold (60%) saving of bill, then lowest ROI within those.
        const savingPool = allCandidates.filter(c => c.savingPct >= savingThreshold).sort((a, b) => a.roi - b.roi);
        if (savingPool.length > 0) {
            packages.push({ ...savingPool[0], optType: 'saving' });
        } else {
            const fallback = [...allCandidates].sort((a, b) => b.savingPct - a.savingPct || a.roi - b.roi);
            packages.push({ ...fallback[0], optType: 'saving' });
        }

        // Standard: max saving with fastest ROI
        const stdSorted = [...allCandidates].sort((a, b) => b.savingMo - a.savingMo || a.roi - b.roi);
        packages.push({ ...stdSorted[0], optType: 'standard' });
    }

    return packages;
}

// Helper to get value regardless of casing quirks
const n = (r, ...keys) => { for (const k of keys) { const v = Number(r[k]); if (v) return v; } return 0; };

// Transform DB pricing rows to ATAP_PRICES format
function toAtapPrices(pricingRows) {
    return pricingRows.map(r => ({
        panels: r.panelQty,
        kwp: Number(r.energy),
        cash: n(r, 'cashPrice', 'CashPrice'),
        cc: n(r, 'cc36Price', 'cC36Price', 'CC36Price'),
        cc60: n(r, 'cc60Price', 'cC60Price', 'CC60Price'),
        cash3p: n(r, 'cash3p8k', 'Cash3p8k'),
        cc36_3p: n(r, 'cc36_3p8k', 'cC36_3p8k', 'CC36_3p8k'),
        cc60_3p: n(r, 'cc60_3p8k', 'cC60_3p8k', 'CC60_3p8k'),
        inverter: r.inverterSize || r.InverterSize || '',
        inverter3p: r.inverterSize3p || r.InverterSize3p || '',
    }));
}

// snapshotId (optional): price off a dated PricingHistory snapshot instead of live/current
// pricing. null/0 → live (default). Re-fetches when it changes. (user 2026-08-03)
export default function useRscPricing(listName = 'Residential Pricing', phaseMode = 'single', panelLimit = 999, snapshotId = null) {
    const [allPricing, setAllPricing] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // Current AFA rate (sen/kWh) from General Settings. Set into the module-level
    // AFA_RATE (so calcC2Bill/calcAtapBill pick it up) AND kept in state so memoized
    // bill/savings computations re-run when it loads (it's a dep below).
    const [afaRate, setAfaRateState] = useState(0);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await getPricing(snapshotId);
                if (!cancelled) setAllPricing(res.data || []);
            } catch (e) {
                if (!cancelled) setError(e.message);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        // AFA — best-effort: never block pricing if it fails (stays 0 = no AFA).
        (async () => {
            try {
                const res = await getAfaSettings();
                const rate = Number(res.data?.currentRate) || 0;
                if (!cancelled) { setAfaRate(rate); setAfaRateState(rate); }
            } catch { /* leave AFA at 0 */ }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [snapshotId]);

    const pricingRows = useMemo(
        () => allPricing.filter(r => r.listName === listName).sort((a, b) => a.panelQty - b.panelQty),
        [allPricing, listName]
    );

    const atapPrices = useMemo(() => toAtapPrices(pricingRows), [pricingRows]);

    // Build battery options from DB (Battery Pricing list) or fallback to defaults
    const batteryOptions = useMemo(() => {
        const battRows = allPricing.filter(r => r.listName === 'Battery Pricing');
        if (battRows.length === 0) return DEFAULT_BATTERY_OPTIONS;
        return battRows.map(r => ({
            type: String(n(r, 'energy', 'Energy')),
            label: `${n(r, 'energy', 'Energy')}kWh`,
            kwhEach: n(r, 'energy', 'Energy'),
            cashEach: n(r, 'cashPrice', 'CashPrice'),
            ccEach: n(r, 'cc36Price', 'cC36Price', 'CC36Price'),
            cc60Each: n(r, 'cc60Price', 'cC60Price', 'CC60Price'),
            name: r.note || `${n(r, 'energy', 'Energy')}kWh`,
        }));
    }, [allPricing]);

    // Generate packages for EVERY battery option dynamically.
    // Large batteries (>=10kWh) try qty 1,2,3; small (<10kWh) try 1,2.
    // Option B threshold = 60% saving-to-bill (falls back to max savingPct
    // when no candidate hits 60%).
    const batteryPackages = useMemo(() => {
        if (!pricingRows.length || !batteryOptions.length) return [];
        // Filter rows by panelLimit BEFORE generation so 'roi'/'saving'/'standard' selections
        // at each bill bracket are picked from valid (within-limit) options. Otherwise post-hoc
        // filtering can drop the winner of a bracket and cause getFor to fall forward to a
        // higher bill/usage bracket — producing inflated savings vs the user's real bill.
        const validRows = phaseMode === '3phase' ? pricingRows : pricingRows.filter(r => !r.panelQty || r.panelQty <= panelLimit);
        return batteryOptions.map(batt => {
            const isLarge = batt.kwhEach >= 10;
            const qtys = isLarge ? [1, 2, 3, 4, 5] : [1, 2];
            return { battery: batt, packages: generatePackages(validRows, batt, qtys, phaseMode, 0.60) };
        });
        // afaRate is a dep so packages recompute (savings/ROI) once the AFA rate loads.
    }, [pricingRows, phaseMode, batteryOptions, panelLimit, afaRate]);

    // Available list names for switching
    const listNames = useMemo(
        () => [...new Set(allPricing.map(r => r.listName))].filter(n => n !== 'Battery Pricing').sort(),
        [allPricing]
    );

    return {
        loading, error,
        atapPrices,
        batteryPackages,
        listNames,
        pricingRows,
        batteryOptions,
        afaRate,
    };
}

// Export utilities for use in the calculator
export { billToKwh, kwhToBill, calcSaving, calcC2Bill, generateCandidates, DEFAULT_BATTERY_OPTIONS as BATTERY_OPTIONS, PANEL_WATT, BATT_RTE, BATT_DOD, YIELD_PER_KWP, RATIO_CAP_NO_BATT, RATIO_CAP_WITH_BATT, RATIO_MORE_BATT, SEDA_CAP_1P, SEDA_CAP_3P, AUTO_UPGRADE_MAX_3P, parseInverterKw, computeSafetyRatio, pickUpgrade3P, computeBatteryCaps, capOk };

// ─────────────────────────────────────────────────────────────────────────
// Commercial pricing (2026-06 list). Solis = GoodWe String rates, String-only, any size
// (re-added per user 2026-07-27 as a selectable brand that prints "Solis"). Brands: 'goodwe' | 'solis' | 'huawei'.
// type: 'string' | 'hybrid'. Battery systems must be Hybrid (GoodWe only).
//   GoodWe String:  <=30 resi · 30–<100 1750 · 100–400 1700 · >400 1600
//   GoodWe Hybrid:  <=30 resi · 30–<100 1800 · (≥100 no hybrid tier → String)
// The old 26–30 commercial band (1925 String / 1975 Hybrid) is GONE: everything up to and
// including 30 kWp now prices off the residential row. The residential list runs to
// 48.75 kWp / 75 panels, so those rows exist. (user 2026-10-14: "<30kwp all use resi price")
//   Huawei String:  100–400 1800 · >400 1700   (N/A <100; String only)
// Returns 0 when the (kWp, brand) combination is outside that brand's range.
// ─────────────────────────────────────────────────────────────────────────
export function commercialPricePerKwp(kwp, brand, type = 'string', residentialPerKwp = 0) {
    const k = Number(kwp) || 0;
    const r = Number(residentialPerKwp) || 0;
    const b = (brand || 'goodwe').toLowerCase();
    const t = (type || 'string').toLowerCase();
    if (k <= 0) return 0;
    if (b === 'huawei') {                 // GoodWe String rate + RM100/kWp; <=30 = residential row
        if (k <= 30) return r;
        if (k < 100) return 1850;         // 1750 + 100
        if (k <= 400) return 1800;        // 1700 + 100
        return 1700;                      // 1600 + 100
    }
    if (b === 'solis') {                  // Solis = GoodWe String rates, String-only, any size (user 2026-07-27)
        if (k <= 30) return r;
        if (k < 100) return 1750;
        if (k <= 400) return 1700;
        return 1600;
    }
    // GoodWe
    if (k <= 30) return r;                // residential row drives everything up to 30 kWp
    if (t === 'hybrid') {
        if (k < 100) return 1800;
        if (k <= 400) return 1700;        // no hybrid tier ≥100 → fall back to String
        return 1600;
    }
    if (k < 100) return 1750;             // GoodWe String
    if (k <= 400) return 1700;
    return 1600;
}

// Brands available for a given kWp. All three at any size — Huawei selectable at any
// kWp (String-only), priced String+RM100/kWp (≥26) or residential+RM2000 (<26). (2026-08-04)
export function availableBrandsForKwp() {
    return ['goodwe', 'solis', 'huawei'];
}

// Inverter types available for a (kWp, brand). Huawei = String only; GoodWe = both.
export function availableTypesForKwp(kwp, brand) {
    const bb = (brand || 'goodwe').toLowerCase();
    if (bb === 'huawei' || bb === 'solis') return ['string'];   // Huawei + Solis are String-only
    return ['hybrid', 'string'];
}

// Default brand — GoodWe everywhere (cheaper ≥100); Huawei is an explicit pick.
export function defaultBrandForKwp() {
    return 'goodwe';
}

// Battery pricing per kWh (Latest 2025-08-05 list).
//   <100 kWh    = RM  800/kWh  (14.3 kWh / unit, hybrid-inverter coupled)
//   100–1000    = RM 1200/kWh  (AC couple, own PCS)
//   >1000       = RM 1100/kWh  (AC couple, own PCS)
export function batteryPricePerKwh(kwh) {
    const n = Number(kwh) || 0;
    if (n <= 0) return 0;
    if (n < 100) return 800;
    if (n <= 1000) return 1200;
    return 1100;
}
